import type {ReferenceHeader, References, Reference} from "@t/fd/editor/References.ts";
import type {DataMaskingTest, AndMaskingTest} from "@t/fd/layout/Masked.ts";
import type {Suggester} from "@t/fd/editor/Suggester.ts";
import {SrcType} from "@t/meta/SourceType.ts";
import {FormEnvironment} from "@fd/Types.ts";

	
export class RefFactory {
	
	private refArray: Reference[];
	
	constructor(env) {
		this.refArray = this.initArray(env);
	}
	
	private initArray(env: FormEnvironment): Reference[] {
		return [
		{
			type: "reference",
			label: env.msg("contact.customer"),
			test: <AndMaskingTest>{ on: "and", d: "customer", 
			tests: [<DataMaskingTest>{ on: "data", d: "customer" },
					<DataMaskingTest>{ on: "data", d: "person" },
					<DataMaskingTest>{ on: "data", d: "branch" }]},
			view: <ReferenceHeader>{
				type: "reference-header",
				params: [{name:"customer",source:"data",d:"customer"},
						{name:"person",source:"data",d:"person"},
						{name:"branch",source:"data",d:"branch"}],
				url: this.apiUrl(SrcType.PARTY)
			},
			edit: <Suggester>{
				type: "suggest",
				property: "customer",
				url: "suggestfeed.do",
				suggester: "PersonSuggester",
				minTags: 0,
				maxTags: 1,
				pickerLabel: env.msg("contact.customer"),
				srcType: SrcType.PARTY,
				srcIdProperty: ["person", "branch", "customer"],
				propagate: [
					{
						d: "customer",
						s: "id.main",
						c: "integer",
						empty: 0
					},
					{
						d: "branch",
						s: "id.branch",
						c: "integer",
						empty: 0,
						ifmissing: 0
					},
					{
						d: "person",
						s: "id.person",
						c: "integer",
						empty: 0,
						ifmissing: 0
					},
					{
						d: "customerFileAs",
						s: "title.main",
						empty: ""
					}
				],
				tag: [
					{
						d: "customer",
						t: "id",
						empty: 0
					},
					{
						d: "customer",
						t: "title",
						empty: 0,
						optional: true
					},
					{
						d: "customerFileAs",
						t: "name",
						empty: ""
					}
				]
			}
		},
		{
			type: "reference",
			label: env.msg("contact.busCase"),
			test: <DataMaskingTest>{ on: "data", d: "busCase" },
			view: <ReferenceHeader>{
				type: "reference-header",
				params: [{name:"id",source:"data",d:"busCase", path: true}],
				url: this.apiUrl(SrcType.BUSCASE)
			},
			edit: <Suggester>{
				type: "suggest",
				property: "busCase",
				url: "suggestfeed.do",
				suggester: "BusCaseSuggester",
				minTags: 0,
				maxTags: 1,
				pickerLabel: env.msg("contact.busCase"),
				srcType: SrcType.BUSCASE,
				propagate: [
					{
						d: "busCase",
						s: "id.main",
						c: "integer",
						empty: 0
					},
					{
						d: "busCaseName",
						s: "title.main",
						empty: ""
					},
					{
						d: "customer",
						s: "id.customer",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "branch",
						s: "id.branch",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "person",
						s: "id.person",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "customerFileAs",
						s: "title.customerFileAs",
						empty: "",
						nooverwrite: true
					}
				],
				tag: [
					{
						d: "busCase",
						t: "id",
						empty: 0
					},
					{
						d: "busCaseName",
						t: "title",
						empty: 0,
						optional: true
					},
					{
						d: "busCaseName",
						t: "name",
						empty: ""
					}
				]
			}
		},
		{
			type: "reference",
			label: env.msg("contact.opportunity"),
			test: <DataMaskingTest>{ on: "data", d: "opportunity" },
			view: <ReferenceHeader>{
				type: "reference-header",
				params: [{name:"id",source:"data",d:"opportunity", path: true}],
				url: this.apiUrl(SrcType.OPPORTUNITY)
			},
			edit: <Suggester>{
				type: "suggest",
				property: "opportunity",
				url: "suggestfeed.do",
				suggester: "OpportunitySuggester",
				minTags: 0,
				maxTags: 1,
				pickerLabel: env.msg("contact.opportunity"),
				srcType: SrcType.OPPORTUNITY,
				propagate: [
					{
						d: "opportunity",
						s: "id.main",
						c: "integer",
						empty: 0
					},
					{
						d: "opportunityName",
						s: "title.main",
						empty: ""
					},
					{
						d: "customer",
						s: "id.customer",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "branch",
						s: "id.branch",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "person",
						s: "id.person",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "customerFileAs",
						s: "title.customerFileAs",
						empty: "",
						nooverwrite: true
					}		
				],
				tag: [
					{
						d: "opportunity",
						t: "id",
						empty: 0
					},
					{
						d: "opportunityName",
						t: "title",
						empty: 0,
						optional: true
					},
					{
						d: "opportunityName",
						t: "name",
						empty: ""
					}
				]
			}
		},
		{
			type: "reference",
			label: env.msg("contact.offer"),
			test: <DataMaskingTest>{ on: "data", d: "offer" },
			view: <ReferenceHeader>{
				type: "reference-header",
				params: [{name:"id",source:"data",d:"offer", path: true}],
				url: this.apiUrl(SrcType.OFFER)
			},
			edit: <Suggester>{
				type: "suggest",
				property: "offer",
				url: "suggestfeed.do",
				suggester: "OfferSuggester",
				minTags: 0,
				maxTags: 1,
				pickerLabel: env.msg("contact.offer"),
				srcType: SrcType.OFFER,
				propagate: [
					{
						d: "offer",
						s: "id.main",
						c: "integer",
						empty: 0
					},
					{
						d: "offerName",
						s: "title.main",
						empty: ""
					},
					{
						d: "customer",
						s: "id.customer",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "branch",
						s: "id.branch",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "person",
						s: "id.person",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "customerFileAs",
						s: "title.customerFileAs",
						empty: "",
						nooverwrite: true
					}
				],
				tag: [
					{
						d: "offer",
						t: "id",
						empty: 0
					},
					{
						d: "offerName",
						t: "title",
						empty: 0,
						optional: true
					},
					{
						d: "offerName",
						t: "name",
						empty: ""
					}
				]
			}
		},
		{
			type: "reference",
			label: env.msg("title.problem"),
			test: <DataMaskingTest>{ on: "data", d: "problem" },
			view: <ReferenceHeader>{
				type: "reference-header",
				params: [{name:"id",source:"data",d:"problem", path: true}],
				url: this.apiUrl(SrcType.PROBLEM)
			},
			edit: <Suggester>{
				type: "suggest",
				property: "problem",
				url: "suggestfeed.do",
				suggester: "ProblemSuggester",
				minTags: 0,
				maxTags: 1,
				pickerLabel: env.msg("title.problem"),
				srcType: SrcType.PROBLEM,
				propagate: [
					{
						d: "problem",
						s: "id.main",
						c: "integer",
						empty: 0
					},
					{
						d: "problemName",
						s: "title.main",
						empty: ""
					},
					{
						d: "customer",
						s: "id.customer",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "branch",
						s: "id.branch",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "person",
						s: "id.person",
						cast: "integer",
						empty: 0,
						ifMissing: 0,
						nooverwrite: true
					},
					{
						d: "customerFileAs",
						s: "title.customerFileAs",
						empty: "",
						nooverwrite: true
					}
				],
				tag: [
					{
						d: "problem",
						t: "id",
						empty: 0
					},
					{
						d: "problemName",
						t: "title",
						empty: 0,
						optional: true
					},
					{
						d: "problemName",
						t: "name",
						empty: ""
					}
				]
			}
		},
		{
			type: "reference",
			label: env.msg("form.editCampaign"),
			test: <DataMaskingTest>{ on: "data", d: "campaign" },
			view: <ReferenceHeader>{
				type: "reference-header",
				params: [{name:"id",source:"data",d:"campaign", path: true}],
				url: this.apiUrl(SrcType.CAMPAIGN)
			},
			edit: <Suggester>{
				type: "suggest",
				property: "campaign",
				url: "suggestfeed.do",
				suggester: "CampaignSuggester",
				minTags: 0,
				maxTags: 1,
				pickerLabel: env.msg("form.editCampaign"),
				srcType: SrcType.CAMPAIGN,
				propagate: [
					{
						d: "campaign",
						s: "id.main",
						c: "integer",
						empty: 0
					},
					{
						d: "campaignName",
						s: "title.main",
						empty: ""
					}
				],
				tag: [
					{
						d: "campaign",
						t: "id",
						empty: 0
					},
					{
						d: "campaignName",
						t: "title",
						empty: 0,
						optional: true
					},
					{
						d: "campaignName",
						t: "name",
						empty: ""
					}
				]
			}
		}
		];
	}

	public getRefs(srcTypes: number[]): Reference[] {
		return srcTypes
			.map(id => this.refArray.find(ref => ref.edit.srcType === id))
			.filter((ref): ref is Reference => ref !== undefined);
	}
	
	private apiUrl(srcType: SrcType): string  {
		if (import.meta.env.DEV) {
			return "api/object/header?srcType="+srcType;
		}
		else {
			switch (srcType) {
				case SrcType.PARTY: return "api/parties/header";
				case SrcType.BUSCASE: return "api/buscases/{id}/header";
				case SrcType.OPPORTUNITY: return "api/opportunities/{id}/header";
				case SrcType.OFFER: return "api/offers/{id}/header";
				case SrcType.PROBLEM: return "api/problems/{id}/header";
				case SrcType.CAMPAIGN: return "api/campaigns/{id}/header";
				default: console.log("Undefined srcType apiUrl:"+srcType);
						return null;
			}
		}
	}
}